import type { MenuProps } from 'antd';
import { Dropdown } from 'antd';
import { LogOut, UserCircle } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';
import { useLoginDialog } from '@/contexts/LoginDialogContext';
import {
  adminNavigationPath,
  isTuttiV2NavigationPath,
  TUTTI_V2_NAVIGATION,
} from '@/navigation/tuttiV2';
import { authStore, brandStore } from '@/stores';
import { copyUserId } from '../common/UserIdMenu';
import { Breadcrumb } from './Breadcrumb';

export const Header = observer(() => {
  const { openDialog } = useLoginDialog();
  const location = useLocation();
  const isTuttiV2Context = isTuttiV2NavigationPath(location.pathname);

  const handleLogout = async () => {
    await authStore.logout();
  };

  const handleLoginClick = () => {
    openDialog();
  };

  const getAvatarDisplay = () => {
    if (authStore.user?.avatar) {
      return (
        <img
          src={authStore.user.avatar}
          alt={authStore.user.name || authStore.user.email}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    if (authStore.user?.name) {
      // 显示首字母头像
      const initial = authStore.user.name.charAt(0).toUpperCase();
      return (
        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
          {initial}
        </div>
      );
    }
    return <UserCircle className="w-8 h-8 text-muted-foreground" />;
  };

  const currentUserId = authStore.user?.user_id;
  const menuItems: MenuProps['items'] = [
    ...(currentUserId
      ? [
          {
            key: 'copy-user-id',
            label: '复制 UID',
            onClick: () => void copyUserId(currentUserId),
          },
        ]
      : []),
    {
      key: 'logout',
      label: (
        <div className="flex items-center cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          退出登录
        </div>
      ),
      onClick: handleLogout,
    },
  ];

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 px-6">
      <div className="flex items-center gap-4">
        <Breadcrumb />
        {isTuttiV2Context ? (
          <Link
            to={adminNavigationPath(TUTTI_V2_NAVIGATION.routes.root)}
            className="rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-gray-50"
          >
            当前 Brand: TUTTI V2
          </Link>
        ) : brandStore.activeBrand ? (
          <Link
            to="/brand-selection"
            className="rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-gray-50"
          >
            当前 Brand: {brandStore.activeBrand.name}
          </Link>
        ) : (
          <Link
            to="/brand-selection"
            className="rounded-full border border-border bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground hover:bg-gray-50"
          >
            未选择 Brand
          </Link>
        )}
      </div>
      <div className="flex items-center gap-2">
        {authStore.isAuthenticated ? (
          <Dropdown
            menu={{ items: menuItems }}
            placement="bottomRight"
            trigger={['click', 'contextMenu']}
          >
            <button
              type="button"
              aria-label="当前用户菜单"
              className="flex items-center gap-2 rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              {getAvatarDisplay()}
            </button>
          </Dropdown>
        ) : (
          <button
            type="button"
            onClick={handleLoginClick}
            className="flex items-center gap-2 rounded-full p-1 hover:bg-gray-100 transition-colors"
          >
            <UserCircle className="w-8 h-8 text-muted-foreground" />
          </button>
        )}
      </div>
    </header>
  );
});
